import { useLayoutEffect, useRef, useState } from 'react'

// One line of text that shrinks until it fits its box, instead of clipping to
// an ellipsis.
//
// This exists because the board is read from across a room. "RB · I… · BYE 13"
// costs a datum outright, and "B. Robin…" costs the name; the same line set a
// point or two smaller costs nothing anyone can perceive at that distance. The
// cells are a fixed grid that must never grow, so something has to give — this
// makes it the type size rather than the content.
//
// MIN_SCALE is the floor. Below it the line would be smaller than the board's
// other meta text by enough to look like a mistake, so a pathological string
// clips instead — the lesser of two bad outcomes, and one no real name reaches.
const MIN_SCALE = 0.62

// Shrinking is measured, not guessed: text width is close enough to linear in
// font-size that one pass lands within a pixel, and the result is clamped so a
// rounding error can't feed back into a resize loop.
interface Props {
  text: string
  className?: string
}

export default function FitText({ text, className = '' }: Props) {
  const ref = useRef<HTMLSpanElement>(null)
  const [scale, setScale] = useState(1)

  useLayoutEffect(() => {
    const span = ref.current
    const box = span?.parentElement
    if (!span || !box) return

    const fit = () => {
      const available = box.clientWidth
      // jsdom and a not-yet-laid-out cell both report 0; leave the scale alone
      // rather than shrinking to the floor on a measurement that means nothing.
      if (available <= 0) return

      setScale((current) => {
        // The span is inline-block and nowrap, so its rect is the text's real
        // width *at the current scale* — divide that back out before deciding.
        const intrinsic = span.getBoundingClientRect().width / current
        if (intrinsic <= 0) return current

        const next = intrinsic > available ? Math.max(MIN_SCALE, available / intrinsic) : 1
        return Math.abs(next - current) < 0.005 ? current : next
      })
    }

    fit()
    // The board is vw-sized, so every cell re-fits on a window resize. Observing
    // the box rather than the span keeps the font-size we set from feeding back
    // in as a resize of the thing being observed.
    //
    // Guarded because jsdom has no ResizeObserver: without it the one-shot fit
    // above still runs, which is all a test environment with no layout can use.
    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(fit)
    observer.observe(box)
    return () => observer.disconnect()
  }, [text])

  return (
    <span
      ref={ref}
      // Deliberately unconstrained: the span has to be free to exceed the box
      // for its rect to report the text's real width. The cell clips it during
      // the frame before the fit lands.
      className={`inline-block whitespace-nowrap ${className}`}
      style={scale === 1 ? undefined : { fontSize: `${scale}em` }}
    >
      {text}
    </span>
  )
}
